import { Tables } from "database.types";
import { fetchApi } from "./base";
import {
  AddVariantToBinderOptions,
  BinderVariant,
  UpdateVariantInBinderOptions,
} from "@/types/binders";

export function createBinder(name: string) {
  return fetchApi<{
    success: boolean;
    data: {
      id: number;
      user_id: string;
      name: string;
      created_at: string;
      updated_at: string | null;
    };
  }>(`/api/binders`, {
    method: "POST",
    body: {
      name,
    },
  });
}

export function getBinders() {
  return fetchApi<{
    success: boolean;
    data: Array<Tables<"binders">>;
  }>(`/api/binders`, {
    method: "GET",
    timeout: 60000,
    retry: 0,
  });
}

export function getBindersWithVariantCount() {
  return fetchApi<{
    success: boolean;
    data: Array<{
      id: number;
      user_id: string;
      name: string;
      created_at: string;
      updated_at?: string;
      variant_count: number;
    }>;
  }>(`/api/with-variant-count`, {
    method: "GET",
  });
}

export function getBinder(id: number) {
  return fetchApi<{
    success: boolean;
    data: {
      id: number;
      user_id: string;
      name: string;
      created_at: string;
      updated_at: string | null;
    };
  }>(`/api/binders/${id}`, {
    method: "GET",
  });
}

export function updateBinder(id: number, name: string) {
  return fetchApi<{
    success: boolean;
    data: {
      id: number;
      user_id: string;
      name: string;
      created_at: string;
      updated_at: string;
    };
  }>(`/api/binders/${id}`, {
    method: "PUT",
    body: {
      name,
    },
  });
}

export function deleteBinder(id: number) {
  return fetchApi(`/api/binders/${id}`, {
    method: "DELETE",
  });
}

export function addVariantToBinder(options: AddVariantToBinderOptions) {
  const body = {
    id: options.binderId,
    variantId: options.variantId,
    printing: options.printing ?? undefined,
    scanned: options.scanned,
    scanId: options.scanId ?? null,
    purchasePrice: options.purchasePrice ?? null,
    language: options.language ?? "english",
    gradingCompanyId: options.gradingCompanyId ?? null,
    gradeId: options.gradeId ?? null,
  };

  return fetchApi(`/api/binders/${options.binderId}/variant`, {
    method: "POST",
    body,
  });
}

export function updateVariantInBinder(options: UpdateVariantInBinderOptions) {
  const body = {
    binderId: options.binderId, // ok
    variantId: options.variantId, // ok
    purchasePrice: options.purchasePrice ?? null, // ok
    gradingCompanyId: options.gradingCompanyId ?? null, // ok
    gradeId: options.gradeId ?? null, // ok
    language: options.language ?? "english",
    marketplaceDescription: options.marketplaceDescription ?? null,
    cardFormat: options.cardFormat,
    condition: options.condition ?? null,
    printing: options.printing,
    gradingCompany: options.gradingCompany ?? null,
    grade: options.grade ?? null,
    relationId: options.relationId, // ok
  };

  return fetchApi(`/api/binders/${options.binderId}/variant`, {
    method: "PUT",
    body,
  });
}

export function removeVariantFromBinder(
  binderId: number | string,
  binderItemId: number | string,
  data?: {
    cause?: string | null;
    payload?: string | null;
  },
) {
  return fetchApi<{
    success: boolean;
    message: string;
  }>(`/api/binders/${binderId}/variants/${binderItemId}`, {
    method: "DELETE",
    body: data || {},
  });
}

export function getBinderVariants(binderId: number) {
  return fetchApi<{
    success: boolean;
    data: Array<BinderVariant>;
  }>(`/api/binders/${binderId}/variants`, {
    method: "GET",
    timeout: 60000,
    retry: 0,
  });
}

export function getBindersOfVariant(variantId: number) {
  return fetchApi<{
    success: boolean;
    data: Array<{
      id: number;
      name: string;
      user_id: string;
      created_at: string;
      updated_at: string;
    }>;
  }>(`/api/binders/by-variant/${variantId}`, {
    method: "GET",
  });
}

export function sortBinderVariants(
  binderId: number,
  sortedVariants: Array<{ variantId: number; position: number }>,
) {
  return fetchApi(`/api/binders/${binderId}/variants/sort`, {
    method: "POST",
    body: sortedVariants,
  });
}

export function reconcileBinderCardLanguages(binderId: number | string) {
  return fetchApi<{
    success: boolean;
    data: {
      binderId: number;
      threshold: number;
      total: number;
      confirmed: number;
      autoUpdated: number;
      reviewNeeded: number;
      reviewNeededIds: number[];
    };
  }>(`/api/binders/${binderId}/reconcile-languages`, {
    method: "POST",
  });
}

export function confirmBinderCardLanguages(
  binderId: number,
  relationIds: number[],
  language: "english" | "japanese" | "chinese",
) {
  return fetchApi(`/api/binders/${binderId}/confirm-languages`, {
    method: "POST",
    body: { relationIds, language },
  });
}
